/**
 * "From last service" evaluation (docs/plans/planned-task-schedule.md
 * §3.9). Not RRULE: next due = last completed + interval, and slipping the
 * job slips the next due.
 *
 * Also the home of the compliance window `[due − early, due + late]`, which every schedule carrying
 * leeway shares: after-service rules and calendar repeats (contract clause 5.3.3 tolerances).
 *
 * The chain seeds from the last completed service, falling back to the legacy
 * `firstDue` and then to the line's effectiveFrom.
 *
 * Month arithmetic clamps to the end of the target month: 31 Jan + 1 month →
 * 28/29 Feb, 29 Feb + 1 year → 28 Feb in a non-leap year.
 */

import { ScheduleAfterServiceRule, ScheduleLeeway, ScheduleLeewayUnit, ScheduleLeewayWindow, ScheduleRule } from './schedule.rule';
import { addDays, daysInMonth, formatDate, isoWeekday } from './schedule.compile';

/** Add whole months, clamping the day to the last valid day of the target month. */
export function addMonthsClamped(date: string, months: number): string {

    const [year, month, day] = date.split('-').map(Number);
    const totalMonths = (year * 12) + (month - 1) + months;
    const targetYear = Math.floor(totalMonths / 12);
    const targetMonth = (totalMonths % 12) + 1;
    const lastDay = daysInMonth(targetYear, targetMonth);

    return formatDate(targetYear, targetMonth, Math.min(day, lastDay));
}

export function addInterval(date: string, interval: number, unit: 'days' | 'months' | 'years'): string {

    switch (unit) {
        case 'days':
            return addDays(date, interval);
        case 'months':
            return addMonthsClamped(date, interval);
        case 'years':
            return addMonthsClamped(date, interval * 12);
    }
}

/**
 * The single next-due date for an after-service schedule, before the
 * non-working-day policy is applied.
 *
 * The seed is `lastCompleted ?? firstDue ?? effectiveFrom`, and the interval is added only once a
 * completion exists. That asymmetry is the point of the mode: with a completion the chain measures
 * FROM the service (last done + 6 months), while without one the seed IS the first due date — adding
 * the interval to it would silently push a brand new task a full cycle past the date the author
 * picked.
 *
 * Two asymmetries, not one, and they pull in opposite directions:
 *
 * - SERVICED (`lastCompleted` set): `lastCompleted + interval` may land in the past and must emit
 *   there. That past date IS the overdue signal, and the engine deliberately skips its effectiveFrom
 *   clamp for this kind so the work stays visible as late rather than silently re-dated.
 * - NEVER SERVICED (no `lastCompleted`): the seed is a date the author picked (or the line's
 *   effectiveFrom), and once that date has passed a frozen seed is invisible — every projection
 *   window runs today-forward, so the task shows up neither as due nor as overdue. So the seed is
 *   clamped forward to `today`: a never-serviced task is due today until someone services it
 *   (WBS-10985). The clamp only ever moves a date forward — a future firstDue is left alone.
 *
 * `firstDue` is the deprecated middle rung, kept only so stored values keep evaluating until the
 * migration clears them.
 */
export function afterServiceNextDue(afterService: ScheduleAfterServiceRule, effectiveFrom: string, today: string, lastCompleted?: string, lastScheduledDue?: string): string {

    const seed = lastCompleted ?? afterService.firstDue ?? effectiveFrom;

    if (!lastCompleted) {
        return seed < today ? today : seed;
    }

    return addInterval(anchorSeed(afterService, lastCompleted, lastScheduledDue), afterService.interval, afterService.unit);
}

/**
 * Which date the next cycle is measured FROM (WBS-11000).
 *
 * 'completion' (the default, and every stored schedule's existing behaviour) measures from the day the
 * work was actually done, so servicing early permanently moves the regime earlier — correct for
 * condition-driven work where the clock restarts on service.
 *
 * 'anniversary' measures from the date the service was DUE, so a smoke-alarm check performed in
 * November to dodge the Christmas period is still due the following December rather than every
 * November thereafter. It needs to know what the completion satisfied; without `lastScheduledDue` there
 * is nothing to anchor to and it degrades to completion anchoring rather than guessing.
 */
function anchorSeed(afterService: ScheduleAfterServiceRule, lastCompleted: string, lastScheduledDue?: string): string {

    if (afterService.anchor !== 'anniversary' || !lastScheduledDue) return lastCompleted;
    return lastScheduledDue;
}

/**
 * Calendar days added past a business-day early-leeway reach to cover public holidays the reach cannot
 * see. A read that over-reaches only fetches a few extra due days; one that under-reaches hides a Visit
 * whose window already opened, so the bound errs wide: a full week covers the Christmas–New Year cluster.
 */
const HOLIDAY_REACH_SLACK_DAYS = 7;

/** Saturday or Sunday, or a listed public holiday: the days a business-day count steps over. */
function isNonBusinessDay(date: string, holidays: ReadonlySet<string>): boolean {

    return isoWeekday(date) >= 6 || holidays.has(date);
}

/**
 * `date` moved by `days` business days, signed. The start date is never counted, so one business day
 * after a Friday (or a Saturday) is the Monday, and one before a Monday is the Friday.
 */
export function addBusinessDays(date: string, days: number, holidays: readonly string[] = []): string {

    const step = days < 0 ? -1 : 1;
    const skip = new Set(holidays);
    let current = date;
    for (let remaining = Math.abs(days); remaining > 0;) {
        current = addDays(current, step);
        if (!isNonBusinessDay(current, skip)) remaining--;
    }
    return current;
}

/** `date` moved by `value` leeway units, signed. `holidays` matters only to 'businessDays'. */
export function addLeeway(date: string, value: number, unit: ScheduleLeewayUnit, holidays?: readonly string[]): string {

    if (unit === 'businessDays') return addBusinessDays(date, value, holidays);
    return addInterval(date, value, unit);
}

/**
 * The leeway pair a schedule carries, or undefined for a kind that has none (per-visit, dates).
 *
 * The one place that knows which kinds hold leeway, so every window derivation (occurrence, Visit card,
 * overdue scan, period reach) reads calendar and after-service tolerance through the same door.
 */
export function scheduleLeeway(schedule: ScheduleRule | undefined): ScheduleLeewayWindow | undefined {

    if (schedule?.kind === 'afterService') return schedule.afterService;
    if (schedule?.kind === 'calendar') return schedule.calendar;
    return undefined;
}

/**
 * Whether a line's public holidays can move this window: only a business-day side counts them.
 *
 * Readers ask before resolving any holidays, so a page with no business-day leeway reads nothing more.
 */
export function usesBusinessDayLeeway(leeway: ScheduleLeewayWindow | undefined): boolean {

    const early = leeway?.earlyLeeway;
    const late = leeway?.lateLeeway;
    const earlyBusinessDays = early?.unit === 'businessDays' && early.value > 0;
    const lateBusinessDays = late?.unit === 'businessDays' && late.value > 0;
    return earlyBusinessDays || lateBusinessDays;
}

/**
 * The furthest due date past `periodEnd` whose early window can still open on or before it.
 *
 * Holiday-agnostic, because callers bound a multi-line read before any line's holidays are known.
 * Holidays only push a business-day window's opening earlier, so the business-day reach adds
 * HOLIDAY_REACH_SLACK_DAYS rather than risk missing a Visit whose window is already open.
 */
export function earlyLeewayReach(periodEnd: string, leeway: ScheduleLeeway): string {

    const reach = addLeeway(periodEnd, leeway.value, leeway.unit);
    return leeway.unit === 'businessDays' ? addDays(reach, HOLIDAY_REACH_SLACK_DAYS) : reach;
}

/**
 * The earliest date a service may legally be performed, given its due date.
 *
 * Without an `earlyLeeway` the window opens on the due date itself, which is the correct reading of a
 * schedule that declares no leeway: nothing earlier counts. `holidays` are the line's public-holiday
 * dates; a business-day leeway without them counts weekends only.
 */
export function leewayWindowOpens(leeway: ScheduleLeewayWindow | undefined, dueDate: string, holidays?: readonly string[]): string {

    const early = leeway?.earlyLeeway;
    if (!early || !(early.value > 0)) return dueDate;
    return addLeeway(dueDate, -early.value, early.unit, holidays);
}

/**
 * The last date a service still counts as on time, given its due date.
 *
 * Without a `lateLeeway` the window closes on the due date, so compliance breaches the day after —
 * the behaviour every schedule had before late tolerance existed.
 */
export function leewayWindowCloses(leeway: ScheduleLeewayWindow | undefined, dueDate: string, holidays?: readonly string[]): string {

    const late = leeway?.lateLeeway;
    if (!late || !(late.value > 0)) return dueDate;
    return addLeeway(dueDate, late.value, late.unit, holidays);
}

/**
 * Whether `date` falls inside the compliance window `[due − early, due + late]`, inclusive at both ends.
 *
 * A date after the window closes is a breach, a different state with different visibility rules
 * (WBS-10921 compliance visibility).
 */
export function isWithinComplianceWindow(leeway: ScheduleLeewayWindow | undefined, dueDate: string, date: string, holidays?: readonly string[]): boolean {

    return date >= leewayWindowOpens(leeway, dueDate, holidays) && date <= leewayWindowCloses(leeway, dueDate, holidays);
}
